import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

import ProxyImage from "@/components/ProxyImage.jsx";
import Button from "@/components/ui/Button.jsx";
import { Avatar, DefItem, Tabs } from "@/components/ui/Display.jsx";
import Tag from "@/components/ui/Tag.jsx";
import { PRODUCTIVITY, toneOf } from "@/lib/tokens.js";
import { formatDateTimeSeconds, formatNumber, formatTime } from "@/lib/format.js";
import { cx } from "@/lib/cx.js";
import { gateMessage } from "@/lib/userDetail.js";
import { formatKeyLogNodes } from "@/lib/keylog.js";
import { useOverlayBehaviour } from "@/hooks/useOverlayBehaviour.js";

/*
 * The detail modal, from the #viewer block in screenshots.html.
 *
 * It is not built on <Modal> because its header is not a title — it is the
 * navigation. ←/→ step through the SAME LIST THE PAGE IS SHOWING, in the order
 * it is showing it, which is why the page passes the already-grouped, already
 * paginated array rather than the raw query result. Stepping past either end
 * does nothing (the buttons disable) instead of wrapping around, so holding an
 * arrow key cannot silently loop the day.
 */
const TABS = [
    { value: "keylog", label: "Key Log" },
    { value: "clipboard", label: "Clipboard" },
];

export default function ScreenshotViewer({
    shots,
    index,
    onIndexChange,
    onClose,
    onExpired,
    // Resolved per SHOT, because an organization-wide list mixes people and the
    // key-log switch is per user. May return null when it cannot be resolved.
    gatesFor,
}) {
    const [tab, setTab] = useState("keylog");
    const panelRef = useOverlayBehaviour({ open: true, onClose });

    const shot = shots[index];

    /*
     * Arrow keys. Capture phase and a guard on the target, so typing in a field
     * that happens to be inside the dialog would not also flick the image —
     * there is none today, but the next person to add one should not have to
     * discover this.
     */
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
            if (
                e.target instanceof HTMLElement &&
                /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)
            )
                return;

            e.preventDefault();
            onIndexChange(e.key === "ArrowLeft" ? index - 1 : index + 1);
        };

        document.addEventListener("keydown", onKeyDown, true);
        return () => document.removeEventListener("keydown", onKeyDown, true);
    }, [index, onIndexChange]);

    if (!shot) return null;

    const p = PRODUCTIVITY[shot.productivityStatus] ?? PRODUCTIVITY.unrated;

    return createPortal(
        <div className="fixed inset-0 z-60">
            <div
                onClick={onClose}
                className="absolute inset-0 bg-slate-950/75 backdrop-blur-sm"
                aria-hidden="true"
            />

            <div className="relative flex h-full items-center justify-center p-4">
                <div
                    ref={panelRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label={`Screenshot of ${shot.user.name} at ${shot.time}`}
                    tabIndex={-1}
                    className="flex max-h-full w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl outline-none dark:border-slate-700 dark:bg-slate-900"
                >
                    {/* ---- Head: who, when, and the navigation ---- */}
                    <div className="flex min-h-14 shrink-0 items-center gap-3 border-b border-slate-200 px-4.5 py-3 dark:border-slate-800">
                        <Avatar name={shot.user.name} size="lg" />
                        <div className="min-w-0">
                            <p className="truncate text-sm font-semibold">{shot.user.name}</p>
                            <p className="truncate text-[12px] text-slate-500 dark:text-slate-400">
                                {formatDateTimeSeconds(shot.timestamp)}
                                {shot.displayCount > 1 &&
                                    ` · display ${shot.display} of ${shot.displayCount}`}
                            </p>
                        </div>

                        <div className="ml-auto flex items-center gap-1.5">
                            <Button
                                className="disabled:opacity-40"
                                title="Previous (←)"
                                aria-label="Previous screenshot"
                                disabled={index === 0}
                                onClick={() => onIndexChange(index - 1)}
                            >
                                <ChevronLeft className="size-4" strokeWidth={2.2} />
                            </Button>

                            <span
                                data-viewer-pos
                                className="w-16 text-center font-mono text-xs text-slate-400"
                            >
                                {index + 1} / {shots.length}
                            </span>

                            <Button
                                className="disabled:opacity-40"
                                title="Next (→)"
                                aria-label="Next screenshot"
                                disabled={index === shots.length - 1}
                                onClick={() => onIndexChange(index + 1)}
                            >
                                <ChevronRight className="size-4" strokeWidth={2.2} />
                            </Button>

                            <div className="mx-1 h-6 w-px bg-slate-200 dark:bg-slate-700" />

                            <Button title="Close (Esc)" aria-label="Close" onClick={onClose}>
                                <X className="size-4" strokeWidth={2.2} />
                            </Button>
                        </div>
                    </div>

                    {/* ---- Body: image + meta ---- */}
                    <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_320px]">
                        <div className="flex items-center justify-center bg-slate-100 p-4 dark:bg-slate-950">
                            <ProxyImage
                                // Keyed by shot, so stepping to the next image
                                // clears the previous one instead of showing it
                                // while the new URL loads.
                                key={shot.key}
                                src={shot.src}
                                alt={shot.title || "Screenshot"}
                                loading="eager"
                                onExpired={onExpired}
                                fallbackLabel="This screenshot is no longer available"
                                // The height cap belongs on the IMAGE, not on
                                // the wrapper. On the wrapper it clips instead:
                                // object-contain can only letterbox within the
                                // element it is applied to, so a tall portrait
                                // screenshot would have its bottom cut off with
                                // nothing to show it had been.
                                fit="contain"
                                className="w-full bg-transparent"
                                imgClassName={cx(
                                    "max-h-[62vh] rounded-lg shadow-sm",
                                    shot.blurred && "shot-blurred",
                                )}
                            />
                        </div>

                        <aside className="flex min-w-0 flex-col border-t border-slate-200 lg:border-t-0 lg:border-l dark:border-slate-800">
                            <div className="space-y-3 p-4.5">
                                <DefItem label="Application">{shot.application}</DefItem>
                                <DefItem label="Window title">
                                    <span className="break-words">{shot.title || "—"}</span>
                                </DefItem>

                                {/* Only browsers report a URL; a blank row here
                                    would read as "no URL was captured" for an
                                    editor that never has one. */}
                                {shot.url && (
                                    <DefItem label="URL">
                                        <span className="font-mono text-[12px] break-all text-brand-600 dark:text-brand-400">
                                            {shot.url}
                                        </span>
                                    </DefItem>
                                )}

                                <div className="flex flex-wrap items-center gap-2 pt-1">
                                    <Tag tone={p.tone}>{p.label}</Tag>
                                    {shot.blurred && <Tag tone="amber">Blurred</Tag>}
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-px border-y border-slate-200 bg-slate-200 dark:border-slate-800 dark:bg-slate-800">
                                <Counter value={shot.keystrokes} label="keystrokes" />
                                <Counter value={shot.mouseClicks} label="clicks" />
                                <Counter value={shot.mouseMovement} label="px moved" />
                            </div>

                            <div className="shrink-0 border-b border-slate-200 pt-3 dark:border-slate-800">
                                <Tabs value={tab} onChange={setTab} options={TABS} />
                            </div>

                            <div className="min-h-32 flex-1 overflow-y-auto p-4">
                                {tab === "keylog" ? (
                                    <KeyLogPanel
                                        keys={shot.keyLog}
                                        gates={gatesFor?.(shot) ?? null}
                                        subject={shot.user?.name}
                                    />
                                ) : (
                                    <ClipboardPanel
                                        items={shot.clipboard}
                                        gates={gatesFor?.(shot) ?? null}
                                        subject={shot.user?.name}
                                    />
                                )}
                            </div>
                        </aside>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}

/*
 * Why this panel is empty, naming the switch that is actually closed.
 *
 * The first version said "off for this organization" whatever the reason, which
 * sent an admin to a setting that was already on while the per-user override
 * was the one holding it shut. See gateMessage in lib/userDetail.js.
 */
function GateNote({ kind, gates, subject }) {
    const { title, hint } = gateMessage(kind, gates, {
        subject: subject || "this user",
    });

    return (
        <div data-gate={kind}>
            <p className="text-[12.5px] text-slate-400 dark:text-slate-500">{title}.</p>
            {hint && (
                <p className="mt-1.5 text-[11.5px] text-slate-400 dark:text-slate-500">{hint}</p>
            )}
        </div>
    );
}

function Counter({ value, label }) {
    return (
        <div className="bg-white p-3 text-center dark:bg-slate-900">
            <p className="font-mono text-base font-semibold">{formatNumber(value)}</p>
            <p className="text-[11px] text-slate-400 dark:text-slate-500">{label}</p>
        </div>
    );
}

/*
 * Both panels below say what is stored as well as what is shown. An empty Key
 * Log looks identical whether nobody typed or the org never turned the feature
 * on, and the difference matters to whoever is reading the screen — so the note
 * stays even when there is data.
 */
function KeyLogPanel({ keys = [], gates, subject }) {
    if (!keys.length) {
        return <GateNote kind="keys" gates={gates} subject={subject} />;
    }

    /*
     * The typed text, not a list of keystrokes.
     *
     * The first version of this panel printed one chip per entry, which for a
     * real interval is several hundred chips spelling a sentence one letter at
     * a time. The reconstruction is the feature — see lib/keylog.js — and the
     * raw stream stays available on the user's Key Log tab.
     */
    const nodes = formatKeyLogNodes(keys);

    return (
        <>
            {nodes.length ? (
                <p
                    data-typed
                    className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 font-mono text-[12px] leading-relaxed break-all whitespace-pre-wrap text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                    {nodes.map((n, i) =>
                        n.type === "text" ? (
                            <span key={i}>{n.value}</span>
                        ) : (
                            <span
                                key={i}
                                title={`Shortcut: ${n.display}${n.label ? ` (${n.label})` : ""}`}
                                className="mx-0.5 inline-block rounded border border-violet-200 bg-violet-50 px-1 align-baseline text-[10.5px] font-semibold text-violet-700 dark:border-violet-800 dark:bg-violet-950/60 dark:text-violet-300"
                            >
                                {n.display}
                                {n.label ? ` ${n.label}` : ""}
                            </span>
                        ),
                    )}
                </p>
            ) : (
                <p className="text-[12.5px] text-slate-400 dark:text-slate-500">
                    {keys.length} key{keys.length === 1 ? "" : "s"} recorded, none of them
                    printable — only navigation and modifiers.
                </p>
            )}

            <p className="mt-3 text-[11.5px] text-slate-400 dark:text-slate-500">
                Every key pressed is recorded, so this is what was typed — password fields
                included. The full key-by-key stream is on this person's Key Log tab.
            </p>
        </>
    );
}

function ClipboardPanel({ items = [], gates, subject }) {
    if (!items.length) {
        return <GateNote kind="clipboard" gates={gates} subject={subject} />;
    }

    return (
        <>
            <div className="space-y-2">
                {items.map((c, i) => (
                    <div
                        key={i}
                        className="rounded-lg border border-slate-200 p-2.5 dark:border-slate-800"
                    >
                        <div className="mb-1.5 flex items-center gap-1.5">
                            <span
                                className={cx(
                                    "tag",
                                    toneOf(c.action === "copy" ? "brand" : "emerald").tag,
                                )}
                            >
                                {c.action}
                            </span>
                            {c.kind === "image" && (
                                <span className={cx("tag", toneOf("violet").tag)}>image</span>
                            )}
                            <span className="ml-auto font-mono text-[10.5px] text-slate-400">
                                {c.timestamp ? formatTime(c.timestamp) : ""}
                            </span>
                        </div>
                        <p className="font-mono text-[11.5px] break-all text-slate-600 dark:text-slate-300">
                            {c.text || (c.kind === "image" ? "(image)" : "")}
                        </p>
                    </div>
                ))}
            </div>
            <p className="mt-3 text-[11.5px] text-slate-400 dark:text-slate-500">
                Image copies store a size marker only — the image itself is never uploaded.
            </p>
        </>
    );
}
